"use client";

import { Pause, Play, Redo2, Repeat, Undo2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ClipFrameThumbs } from "./ClipFrameThumbs";
import { formatClock } from "./format";
import { TimecodeChip } from "./TimecodeChip";
import { HoverStamp, RangeTag } from "./timelineTags";
import { useLoopPlayback } from "./useLoopPlayback";
import { useRubberBand } from "./useRubberBand";
import type { UndoHistory } from "./useUndoHistory";
import type { ZoomView } from "./useZoomView";

type MediaEl = HTMLVideoElement | HTMLAudioElement;

/** Smallest selectable clip, in seconds. */
const MIN_CLIP = 0.05;

/**
 * A clip editor over a source preview: a timeline track with a highlighted
 * selection between two draggable handles, a draggable playhead, click-to-seek,
 * editable in/out timecodes, and — the point of it — **loop playback of the
 * selection** so you trim while watching the clip repeat. Drag in the margin
 * above/below the bar or across the overview lane to zoom; Space plays, I/O set
 * in/out at the playhead, ←/→ nudge, Ctrl/⌘+Z undoes.
 *
 * Concerns live in focused hooks: {@link useLoopPlayback} (playback + loop),
 * {@link useZoomView} (the zoom window), {@link useRubberBand} (the two zoom
 * gestures), and {@link useUndoHistory} (passed in as `history`). This component
 * wires them together and owns the in/out selection editing.
 */
export function ClipTimeline({
  getPreviewElement,
  previewUrl,
  duration,
  start,
  end,
  onChange,
  history,
  zoom,
  file,
  disabled = false,
}: {
  getPreviewElement?: () => MediaEl | null;
  previewUrl: string | null;
  duration: number;
  start: number;
  end: number;
  onChange: (start: number, end: number) => void;
  history: UndoHistory;
  /** The zoom window, owned by the parent so subtitles etc. can drive it. */
  zoom: ZoomView;
  /** Source file, used to decode the in/out frame thumbnails. */
  file?: Blob | null;
  disabled?: boolean;
}) {
  const t = useTranslations("mediaTools.clipTimeline");
  const [loop, setLoop] = useState(true);
  const trackRef = useRef<HTMLDivElement>(null);
  const laneRef = useRef<HTMLDivElement>(null);

  // Latest values for the frame loop + global keys, read through a ref so those
  // subscriptions don't re-bind on every edit.
  const stateRef = useRef({ start, end, loop });
  stateRef.current = { start, end, loop };

  const clampStart = (t: number) => Math.max(0, Math.min(t, stateRef.current.end - MIN_CLIP));
  const clampEnd = (t: number) =>
    Math.min(duration, Math.max(t, stateRef.current.start + MIN_CLIP));

  const { current, playing, seek, playClip } = useLoopPlayback({
    getPreviewElement,
    previewUrl,
    duration,
    stateRef,
  });

  const { viewFrom, viewTo, viewSpan, zoomed, resetView, commitView } = zoom;

  // Which handle a pointer gesture is dragging (null when idle).
  const dragging = useRef<null | "start" | "end" | "playhead">(null);

  // "Scrub" while adjusting in/out: pin the playhead to the boundary being
  // edited and hold it there (paused, no drift). Resume the loop ~500ms after
  // the gesture ends — and never mid-drag while a handle is still held.
  const scrubbing = useRef(false);
  const wasPlaying = useRef(false);
  const resumeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleResume = () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => {
      if (dragging.current) {
        scheduleResume(); // a handle is still held — keep waiting
        return;
      }
      scrubbing.current = false;
      if (wasPlaying.current)
        getPreviewElement?.()
          ?.play()
          .catch(() => {});
    }, 500);
  };
  const scrubTo = (time: number) => {
    const el = getPreviewElement?.();
    if (!el) return;
    if (!scrubbing.current) {
      scrubbing.current = true;
      wasPlaying.current = !el.paused;
    }
    if (!el.paused) el.pause();
    seek(time); // pin the playhead to the boundary
    scheduleResume();
  };
  useEffect(
    () => () => {
      if (resumeTimer.current) clearTimeout(resumeTimer.current);
    },
    []
  );

  // Position on the (possibly zoomed) main bar / on the full-clip overview.
  const pct = (t: number) => Math.max(0, Math.min(100, ((t - viewFrom) / viewSpan) * 100));
  const pctFull = (t: number) =>
    duration > 0 ? Math.max(0, Math.min(100, (t / duration) * 100)) : 0;

  // The control the arrow keys act on: whichever of in / out / playhead was
  // touched most recently.
  const lastEdited = useRef<"start" | "end" | "playhead">("playhead");

  // Coalesce a burst of nudges (held arrow key / repeated clicks) into one undo
  // step: close the history group once the nudging pauses.
  const nudgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endGroupSoon = () => {
    if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    nudgeTimer.current = setTimeout(() => history.endGroup(), 400);
  };
  useEffect(
    () => () => {
      if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
    },
    []
  );

  const nudgeStart = (delta: number) => {
    history.record("nudge-start");
    endGroupSoon();
    const next = clampStart(stateRef.current.start + delta);
    onChange(next, stateRef.current.end);
    scrubTo(next);
  };
  const nudgeEnd = (delta: number) => {
    history.record("nudge-end");
    endGroupSoon();
    const next = clampEnd(stateRef.current.end + delta);
    onChange(stateRef.current.start, next);
    scrubTo(next);
  };
  const nudgeActive = (delta: number) => {
    if (lastEdited.current === "start") nudgeStart(delta);
    else if (lastEdited.current === "end") nudgeEnd(delta);
    else {
      const el = getPreviewElement?.();
      if (el) seek(el.currentTime + delta);
    }
  };

  // Editor keyboard shortcuts (ignored while typing in a field).
  useEffect(() => {
    if (duration <= 0) return;
    const onKey = (event: KeyboardEvent) => {
      const el = getPreviewElement?.();
      if (!el) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      ) {
        return; // let the field's own text undo/editing handle keys
      }
      // Undo / redo (history.* are stable useCallbacks, so this listener stays
      // subscribed without re-binding on every edit).
      if ((event.ctrlKey || event.metaKey) && (event.key === "z" || event.key === "Z")) {
        event.preventDefault();
        if (event.shiftKey) history.redo();
        else history.undo();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && (event.key === "y" || event.key === "Y")) {
        event.preventDefault();
        history.redo();
        return;
      }
      switch (event.key) {
        case " ":
          event.preventDefault();
          playClip();
          break;
        case "i":
        case "I":
          lastEdited.current = "start";
          history.record();
          onChange(clampStart(el.currentTime), stateRef.current.end);
          break;
        case "o":
        case "O":
          lastEdited.current = "end";
          history.record();
          onChange(stateRef.current.start, clampEnd(el.currentTime));
          break;
        case "ArrowLeft":
          event.preventDefault();
          nudgeActive(event.shiftKey ? -1 : -0.1);
          break;
        case "ArrowRight":
          event.preventDefault();
          nudgeActive(event.shiftKey ? 1 : 0.1);
          break;
        case "l":
        case "L":
          setLoop((value) => !value);
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // history.* are stable useCallbacks and playClip/seek/nudge read live state
    // through refs, so they're intentionally not deps (avoids re-binding the
    // listener every frame during playback).
  }, [getPreviewElement, duration, onChange]);

  // --- in/out handle drag (pointer capture; one undo checkpoint per gesture) ---
  const applyAt = (which: "start" | "end" | "playhead", clientX: number) => {
    lastEdited.current = which;
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const t = viewFrom + ratio * viewSpan; // map within the visible window
    if (which === "start") {
      history.record("drag");
      const next = clampStart(t);
      onChange(next, stateRef.current.end);
      scrubTo(next); // pin the playhead to the in point being dragged
    } else if (which === "end") {
      history.record("drag");
      const next = clampEnd(t);
      onChange(stateRef.current.start, next);
      scrubTo(next);
    } else {
      seek(t); // scrubbing the playhead itself does move it
    }
  };

  // --- zoom gestures: bar margin + overview lane (shared rubber-band logic) ---
  const barZoom = useRubberBand({
    rectRef: trackRef,
    toTime: (ratio) => viewFrom + ratio * viewSpan,
    onCommit: commitView,
    disabled,
    canHover: () => dragging.current === null,
  });
  const laneZoom = useRubberBand({
    rectRef: laneRef,
    toTime: (ratio) => ratio * duration,
    onCommit: commitView,
    disabled: disabled || duration <= 0,
  });

  const onHandleDown =
    (which: "start" | "end" | "playhead") => (event: React.PointerEvent<HTMLElement>) => {
      if (disabled) return;
      // Consume the event so a bar/handle interaction never starts a margin zoom-drag.
      event.stopPropagation();
      event.preventDefault();
      barZoom.clearHover();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      dragging.current = which;
      applyAt(which, event.clientX);
    };
  const onHandleMove = (event: React.PointerEvent<HTMLElement>) => {
    if (dragging.current) applyAt(dragging.current, event.clientX);
  };
  const onHandleUp = (event: React.PointerEvent<HTMLElement>) => {
    const which = dragging.current;
    if (!which) return;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    dragging.current = null;
    if (which !== "playhead") {
      history.endGroup();
      scheduleResume(); // resume the loop ~500ms after the gesture ends
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" disabled={disabled} onClick={playClip}>
          {playing ? <Pause className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />}
          {playing ? t("pause") : t("playClip")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant={loop ? "default" : "outline"}
          aria-pressed={loop}
          disabled={disabled}
          onClick={() => setLoop((value) => !value)}
        >
          <Repeat className="mr-2 h-4 w-4" />
          {t("loop")}
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8"
          aria-label={t("undo")}
          title={t("undoTitle")}
          disabled={disabled || !history.canUndo}
          onClick={() => history.undo()}
        >
          <Undo2 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          size="icon"
          variant="outline"
          className="h-8 w-8"
          aria-label={t("redo")}
          title={t("redoTitle")}
          disabled={disabled || !history.canRedo}
          onClick={() => history.redo()}
        >
          <Redo2 className="h-4 w-4" />
        </Button>
        <div className="ml-auto flex items-center gap-4 text-sm text-foreground/60">
          <span>
            {t("clip")}{" "}
            <span className="font-mono text-foreground/80">
              {formatClock(Math.max(0, end - start))}
            </span>
          </span>
          <span>
            {t("playhead")}{" "}
            <span className="font-mono text-foreground/80">{formatClock(current)}</span>
          </span>
        </div>
      </div>

      {/* Side room so the in/out chips stay visible at the extremes. The padded
          area + the bar's margins are the zoom-drag capture zone; the bar and
          grips stop propagation, so they keep scrub/trim. */}
      <div
        className={cn("px-20 py-12 touch-none select-none", !disabled && "cursor-crosshair")}
        {...barZoom.handlers}
        onDoubleClick={resetView}
      >
        <div
          ref={trackRef}
          className={cn(
            "relative h-12 w-full rounded bg-foreground/10",
            disabled ? "opacity-50" : "cursor-pointer"
          )}
          onPointerDown={onHandleDown("playhead")}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
        >
          {barZoom.hover && !barZoom.band && (
            <HoverStamp x={barZoom.hover.x} y={barZoom.hover.y} time={barZoom.hover.t} />
          )}
          {barZoom.band && (
            <>
              <div
                className="pointer-events-none absolute -inset-y-3 z-10 border-foreground/50 border-x-2 bg-foreground/15"
                style={{
                  left: `${pct(Math.min(barZoom.band.from, barZoom.band.to))}%`,
                  width: `${pct(Math.max(barZoom.band.from, barZoom.band.to)) - pct(Math.min(barZoom.band.from, barZoom.band.to))}%`,
                }}
              />
              <RangeTag
                leftPct={(pct(barZoom.band.from) + pct(barZoom.band.to)) / 2}
                from={Math.min(barZoom.band.from, barZoom.band.to)}
                to={Math.max(barZoom.band.from, barZoom.band.to)}
              />
            </>
          )}
          {/* selection highlight + thin in / out / playhead lines */}
          <div
            className="pointer-events-none absolute inset-y-0 bg-foreground/25"
            style={{ left: `${pct(start)}%`, width: `${pct(end) - pct(start)}%` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-foreground"
            style={{ left: `${pct(start)}%` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 w-0.5 -translate-x-1/2 bg-timeline-out"
            style={{ left: `${pct(end)}%` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 w-px -translate-x-1/2 bg-primary"
            style={{ left: `${pct(current)}%` }}
          />

          {/* start grip (triangle above) + editable in-chip above it */}
          <div
            role="slider"
            aria-label={t("clipStart")}
            aria-valuemin={0}
            aria-valuemax={duration}
            aria-valuenow={start}
            tabIndex={disabled ? -1 : 0}
            className="absolute -top-2.5 h-2.5 w-3 -translate-x-1/2 cursor-ew-resize bg-foreground"
            style={{ left: `${pct(start)}%`, clipPath: "polygon(0 0, 100% 0, 50% 100%)" }}
            onPointerDown={onHandleDown("start")}
            onPointerMove={onHandleMove}
            onPointerUp={onHandleUp}
          />
          <div
            className="absolute -top-10 z-20 -translate-x-1/2"
            style={{ left: `${pct(start)}%` }}
          >
            <TimecodeChip
              ariaLabel={t("in")}
              value={start}
              disabled={disabled}
              onCommit={(t) => {
                lastEdited.current = "start";
                history.record();
                onChange(clampStart(t), stateRef.current.end);
              }}
              onStep={(delta) => {
                lastEdited.current = "start";
                nudgeStart(delta);
              }}
            />
          </div>

          {/* end grip (triangle below) + editable out-chip below it */}
          <div
            role="slider"
            aria-label={t("clipEnd")}
            aria-valuemin={0}
            aria-valuemax={duration}
            aria-valuenow={end}
            tabIndex={disabled ? -1 : 0}
            className="absolute -bottom-2.5 h-2.5 w-3 -translate-x-1/2 cursor-ew-resize bg-timeline-out"
            style={{ left: `${pct(end)}%`, clipPath: "polygon(50% 0, 0 100%, 100% 100%)" }}
            onPointerDown={onHandleDown("end")}
            onPointerMove={onHandleMove}
            onPointerUp={onHandleUp}
          />
          <div
            className="absolute -bottom-10 z-20 -translate-x-1/2"
            style={{ left: `${pct(end)}%` }}
          >
            <TimecodeChip
              ariaLabel={t("out")}
              value={end}
              disabled={disabled}
              onCommit={(t) => {
                lastEdited.current = "end";
                history.record();
                onChange(stateRef.current.start, clampEnd(t));
              }}
              onStep={(delta) => {
                lastEdited.current = "end";
                nudgeEnd(delta);
              }}
            />
          </div>
        </div>
      </div>

      {/* Overview minimap: zoom window + in/out across the whole clip, with the
          reset control beside it. Drag across it to choose the zoom window. */}
      <div className="px-20">
        <div className="mb-1 flex items-center gap-3 text-xs text-foreground/40">
          {zoomed ? (
            <div className="ml-auto flex items-center gap-3">
              <span>
                {t("zoomedArea")}{" "}
                <span className="font-mono text-foreground/60">
                  {formatClock(viewFrom)}–{formatClock(viewTo)}
                </span>
              </span>
              <span>
                {t("window")}{" "}
                <span className="font-mono text-foreground/60">{formatClock(viewSpan)}</span>
              </span>
              <button
                type="button"
                className="rounded px-1.5 py-0.5 text-foreground/70 hover:bg-foreground/10"
                onClick={resetView}
              >
                {t("resetZoom")}
              </button>
            </div>
          ) : (
            <span>
              {t("fullClip")}{" "}
              <span className="font-mono text-foreground/60">{formatClock(duration)}</span>
            </span>
          )}
        </div>
        <div
          ref={laneRef}
          className={cn(
            "relative h-4 w-full touch-none select-none rounded bg-foreground/10",
            disabled ? "opacity-50" : "cursor-crosshair"
          )}
          {...laneZoom.handlers}
          onDoubleClick={resetView}
        >
          <div
            className="pointer-events-none absolute inset-y-0 rounded bg-foreground/20"
            style={{
              left: `${pctFull(viewFrom)}%`,
              width: `${pctFull(viewTo) - pctFull(viewFrom)}%`,
            }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 w-0.5 bg-foreground/70"
            style={{ left: `${pctFull(start)}%` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 w-0.5 bg-timeline-out/70"
            style={{ left: `${pctFull(end)}%` }}
          />
          <div
            className="pointer-events-none absolute inset-y-0 w-px bg-primary/80"
            style={{ left: `${pctFull(current)}%` }}
          />
          {laneZoom.band && (
            <>
              <div
                className="pointer-events-none absolute inset-y-0 z-10 rounded border-foreground/50 border-x-2 bg-foreground/25"
                style={{
                  left: `${pctFull(Math.min(laneZoom.band.from, laneZoom.band.to))}%`,
                  width: `${pctFull(Math.max(laneZoom.band.from, laneZoom.band.to)) - pctFull(Math.min(laneZoom.band.from, laneZoom.band.to))}%`,
                }}
              />
              <RangeTag
                leftPct={(pctFull(laneZoom.band.from) + pctFull(laneZoom.band.to)) / 2}
                from={Math.min(laneZoom.band.from, laneZoom.band.to)}
                to={Math.max(laneZoom.band.from, laneZoom.band.to)}
              />
            </>
          )}
          {laneZoom.hover && !laneZoom.band && (
            <HoverStamp x={laneZoom.hover.x} y={laneZoom.hover.y} time={laneZoom.hover.t} />
          )}
        </div>
      </div>

      {file && <ClipFrameThumbs file={file} start={start} end={end} duration={duration} />}

      <p className="text-xs text-foreground/50">
        {t("help.beforeShift")} <kbd className="font-mono">Shift</kbd> {t("help.afterShift")}{" "}
        <kbd className="font-mono">I</kbd> / <kbd className="font-mono">O</kbd> {t("help.afterIO")}{" "}
        <kbd className="font-mono">Space</kbd> {t("help.afterSpace")}{" "}
        <kbd className="font-mono">Ctrl/⌘+Z</kbd> {t("help.afterUndo")}
      </p>
    </div>
  );
}
